import { type OpenMode, openModeOf, type RenderedCitation } from "@esi/shared";
import { useDocuments } from "../projects/queries.ts";
import type { ViewerTarget } from "../projects/SourceViewer.tsx";
import { useAttachmentContext } from "./attachments/AttachmentControls.tsx";

/**
 * How a cited file opens (openspec change `open-in-browser`): a new tab or the viewer for a ready
 * file of a compatible type, else null (wiki pages keep their link, other files download). The
 * type comes from the document or attachment list the conversation already holds (no fetch).
 */
export function useCitationOpen(
  citation: RenderedCitation | undefined,
  projectId: string,
  conversationId: string | undefined,
): { mode: OpenMode; target: ViewerTarget } | null {
  const attachments = useAttachmentContext();
  const documents = useDocuments(projectId, false);
  if (!citation || citation.title === null || citation.kind === "wiki_page") {
    return null;
  }
  if (citation.attachment) {
    const file = attachments?.list?.attachments.find((a) => a.id === citation.documentId);
    const mode =
      file?.status === "ready" && file.downloadAllowed ? openModeOf(file.mediaType) : null;
    return file && mode && conversationId
      ? {
          mode,
          target: {
            kind: "attachment",
            conversationId,
            attachmentId: file.id,
            title: file.title,
          },
        }
      : null;
  }
  const document = projectId
    ? documents.data?.documents.find((d) => d.id === citation.documentId)
    : undefined;
  const mode = document?.status === "ready" ? openModeOf(document.mediaType) : null;
  return document && mode
    ? {
        mode,
        target: { kind: "document", projectId, documentId: document.id, title: document.title },
      }
    : null;
}
